import React, { Fragment } from "react";
import "./navbar.less";
import { NavLink, useNavigate } from "react-router-dom";
import { http, logoutURL } from "../../services/http";
import { useAuth } from "../../auth/AuthContext";
import logoImage from "../../assets/img/logo/logo.png";

// Simple SVG icon components
const Icon = ({ d }) => (
  <svg className="nav-icon" viewBox="0 0 24 24"><path d={d} /></svg>
);
const DashboardIcon = () => <Icon d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />;
const ChartIcon = () => <Icon d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" />;
const CloudIcon = () => <Icon d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />;
const YouTubeIcon = () => <Icon d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z" />;
const PeopleIcon = () => <Icon d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />;
const PersonIcon = () => <Icon d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />;
const BarChartIcon = () => <Icon d="M5 9.2h3V19H5zM10.6 5h2.8v14h-2.8zm5.6 8H19v6h-2.8z" />;
const LogoutIcon = () => <Icon d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />;

const Nav = () => {
  const activeClassName = "nav-active";
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await http.post(logoutURL());
    } catch (e) { /* ignore */ }
    logout();
    navigate("/sign-in");
  };

  const isStakeholder = user?.username === 'stakeholder';

  const navItems = isStakeholder ? [
    { to: '/', name: 'KPI', icon: <DashboardIcon /> },
  ] : [
    { to: '/', name: 'Home', icon: <DashboardIcon /> },
    { to: '/analysis-details', name: 'Analysis Details', icon: <CloudIcon /> },
    { to: '/data/daily', name: 'Daily (90 days)', icon: <ChartIcon /> },
    { to: '/data/weekly', name: 'Weekly (1 year)', icon: <ChartIcon /> },
    { to: '/data/monthly', name: 'Monthly (1 year)', icon: <ChartIcon /> },
    { to: '/data/all-time', name: 'All time', icon: <ChartIcon /> },
    { to: '/youtube-imports', name: 'YouTube Imports', icon: <YouTubeIcon /> },
    { to: '/user-segments-studio', name: 'Studio Segments', icon: <PeopleIcon /> },
    { to: '/user-segments-songwriter', name: 'Songwriter Segments', icon: <PeopleIcon /> },
    { to: '/segment-events-studio', name: 'Studio Events', icon: <PersonIcon /> },
    { to: '/segment-events-songwriter', name: 'Songwriter Events', icon: <PersonIcon /> },
    { to: '/onboarding-funnel', name: 'Onboarding Funnel', icon: <BarChartIcon /> },
    { to: '/cac', name: 'CAC Calculator', icon: <BarChartIcon /> },
    { to: '/kpi', name: 'KPI', icon: <BarChartIcon /> },
  ];

  return (
    <nav>
      <a className="brand" href="/">
        <img src={logoImage} width="40" height="35" alt="" />
        <span>Dashboard</span>
      </a>
      <hr />
      {navItems.map(({ to, name, icon }) => (
        <NavLink
          key={to}
          to={to}
          end
          className={({ isActive }) => (isActive ? activeClassName : undefined)}
        >
          <div className="item">
            {icon}
            {name.toUpperCase()}
          </div>
        </NavLink>
      ))}
      {user ? (
        <Fragment>
          <hr />
          <NavLink
            to="/sign-in"
            className={({ isActive }) => (isActive ? activeClassName : undefined)}
            onClick={async (e) => { e.preventDefault(); await handleSignOut(); }}
          >
            <div className="item">
              <LogoutIcon />
              Sign Out
            </div>
          </NavLink>
        </Fragment>
      ) : null}
    </nav>
  );
};

export default Nav;
